import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ArrowUpRight, Gift, Sparkles } from "lucide-react";

const TIER_STYLE = {
  Silver: "bg-slate-500/20 text-slate-200 border-slate-500/30",
  Gold: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  Platinum: "bg-cyan-500/20 text-cyan-200 border-cyan-500/30",
  Black: "bg-neutral-800 text-white border-white/20",
  Elite: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30",
};

const ROLE_STYLE = {
  affiliate: "bg-pink-500/20 text-pink-300 border-pink-500/30",
  customer: "bg-slate-500/20 text-slate-300 border-slate-500/30",
};

const STATUS_STYLE = {
  pending: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  processed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
};

export default function TierUpgrades() {
  const [data, setData] = useState({ upgrades: [], total: 0, pending_count: 0, processed_count: 0 });
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (role !== "all") params.role = role;
    if (status !== "all") params.gift_status = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/admin/tier-upgrades", { params }).then((r) => setData(r.data));
  }, [role, status, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const open = (row) => {
    setSelected(row);
    setNote(row.processed_note || "");
  };
  const close = () => { setSelected(null); setNote(""); };

  const markProcessed = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/admin/tier-upgrades/${selected.id}/process`, { note: note.trim() || null });
      toast.success("Gift marked as processed");
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const resetFilters = () => { setRole("all"); setStatus("all"); setDateFrom(""); setDateTo(""); };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="velo-card p-4">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider">Total upgrades</div>
          <div className="text-2xl text-white font-medium mt-1" data-testid="tier-total">{data.total}</div>
        </div>
        <div className="velo-card p-4">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><Gift className="w-3 h-3 text-amber-400" /> Gifts pending</div>
          <div className="text-2xl text-amber-300 font-medium mt-1" data-testid="tier-pending">{data.pending_count}</div>
        </div>
        <div className="velo-card p-4">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-emerald-400" /> Gifts processed</div>
          <div className="text-2xl text-emerald-300 font-medium mt-1" data-testid="tier-processed">{data.processed_count}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="velo-card p-4 flex flex-col sm:flex-row gap-3 flex-wrap items-end">
        <div className="flex-1 min-w-[140px]">
          <Label className="text-slate-400 text-[11px] uppercase tracking-wider">From</Label>
          <Input data-testid="tier-filter-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="velo-input mt-1.5 h-10" />
        </div>
        <div className="flex-1 min-w-[140px]">
          <Label className="text-slate-400 text-[11px] uppercase tracking-wider">To</Label>
          <Input data-testid="tier-filter-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="velo-input mt-1.5 h-10" />
        </div>
        <div className="flex-1 min-w-[140px]">
          <Label className="text-slate-400 text-[11px] uppercase tracking-wider">User type</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="tier-filter-role" className="velo-input mt-1.5 h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#22103F] border-white/10 text-white">
              <SelectItem value="all">All users</SelectItem>
              <SelectItem value="customer">Customer</SelectItem>
              <SelectItem value="affiliate">Affiliate</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <Label className="text-slate-400 text-[11px] uppercase tracking-wider">Gift status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="tier-filter-status" className="velo-input mt-1.5 h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#22103F] border-white/10 text-white">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="processed">Processed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="tier-filter-reset" variant="outline" onClick={resetFilters} className="rounded-full border-white/10 bg-transparent hover:bg-white/5 text-slate-300 h-10">Reset</Button>
      </div>

      {/* Table */}
      <div className="velo-card overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead className="bg-[#170929] border-b border-white/10">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-6 py-4">Member</th>
              <th className="px-6 py-4">User type</th>
              <th className="px-6 py-4">Upgrade</th>
              <th className="px-6 py-4">Upgraded on</th>
              <th className="px-6 py-4">Gift status</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {data.upgrades.map((r) => {
              const prevCls = TIER_STYLE[r.previous_tier] || TIER_STYLE.Silver;
              const newCls = TIER_STYLE[r.new_tier] || TIER_STYLE.Silver;
              const roleCls = ROLE_STYLE[r.user_role] || ROLE_STYLE.customer;
              const statusCls = STATUS_STYLE[r.gift_status] || STATUS_STYLE.pending;
              return (
                <tr key={r.id} data-testid={`tier-upgrade-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-sm text-white">{r.user_name}</div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">{r.user_phone}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${roleCls}`}>{r.user_role}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${prevCls}`}>{r.previous_tier}</span>
                      <ArrowUpRight className="w-3.5 h-3.5 text-pink-400" strokeWidth={2} />
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${newCls}`}>{r.new_tier}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-xs text-slate-400">{format(new Date(r.created_at), "dd MMM yy, HH:mm")}</td>
                  <td className="px-6 py-4">
                    <Badge className={`border ${statusCls} capitalize`}>{r.gift_status}</Badge>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Button data-testid={`tier-open-${r.id}`} size="sm" variant="outline" onClick={() => open(r)} className="rounded-full border-white/10 bg-transparent hover:bg-white/5 text-slate-200 h-8">Open</Button>
                  </td>
                </tr>
              );
            })}
            {data.upgrades.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500 text-sm">No tier upgrades match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="bg-[#22103F] border-white/10 text-white max-w-lg" data-testid="tier-detail-dialog">
          <DialogHeader><DialogTitle>Tier upgrade · gift</DialogTitle></DialogHeader>
          {selected ? (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="velo-surface p-3 rounded">
                  <div className="text-xs text-slate-500 uppercase">Member</div>
                  <div className="text-sm text-white mt-1">{selected.user_name}</div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5">{selected.user_phone}</div>
                </div>
                <div className="velo-surface p-3 rounded">
                  <div className="text-xs text-slate-500 uppercase">User type</div>
                  <div className="text-sm text-white mt-1 capitalize">{selected.user_role}</div>
                </div>
              </div>
              <div className="velo-surface p-4 rounded-lg">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Upgrade</div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${TIER_STYLE[selected.previous_tier] || TIER_STYLE.Silver}`}>{selected.previous_tier}</span>
                  <ArrowUpRight className="w-4 h-4 text-pink-400" strokeWidth={2} />
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${TIER_STYLE[selected.new_tier] || TIER_STYLE.Silver}`}>{selected.new_tier}</span>
                </div>
                <div className="text-xs text-slate-500 mt-3">
                  {format(new Date(selected.created_at), "dd MMM yyyy, HH:mm")}
                  {selected.source_reference ? <span> · {selected.source_reference}</span> : null}
                </div>
                <div className="text-xs text-slate-400 mt-1 font-mono">Total earned: {selected.total_earned?.toLocaleString("en-IN")} pts</div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Gift note (optional)</Label>
                <Textarea
                  data-testid="tier-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Handed over Veloskin cap at Kochi outlet on 20 Feb"
                  className="velo-input mt-2 min-h-[80px]"
                  disabled={selected.gift_status === "processed"}
                />
                {selected.gift_status === "processed" && selected.processed_at ? (
                  <div className="text-xs text-emerald-400 mt-2">Processed on {format(new Date(selected.processed_at), "dd MMM yy, HH:mm")}</div>
                ) : null}
              </div>
            </div>
          ) : null}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={close} className="rounded-full border-white/10 bg-transparent hover:bg-white/5 text-slate-300">Close</Button>
            {selected && selected.gift_status === "pending" ? (
              <Button data-testid="tier-mark-processed" onClick={markProcessed} disabled={busy} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">
                {busy ? "Saving..." : "Mark gift as processed"}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
